import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Dumbbell, Plus, ChevronRight, UserPlus, Scale,
  TrendingUp, TrendingDown, AlertTriangle, Droplet, MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const DashboardPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [progress, setProgress] = useState(null);
  const [latestBodyWeight, setLatestBodyWeight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [dietCount, setDietCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  useEffect(() => {
    fetchData();
    apiFetch('/api/notifications/count')
      .then((r) => r.ok ? r.json() : { count: 0, diet_count: 0 })
      .then((d) => {
        setNotifCount(d.count || 0);
        setDietCount(d.diet_count || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (showCreateOptions) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showCreateOptions]);

  const fetchData = async () => {
    try {
      const [workoutsRes, progressRes, bodyWeightRes] = await Promise.all([
        apiFetch("/api/workouts"),
        apiFetch("/api/dashboard/stats"),
        apiFetch("/api/body-weights/latest"),
      ]);
      if (workoutsRes.ok) setWorkouts(await workoutsRes.json());
      if (progressRes.ok) setProgress(await progressRes.json());
      if (bodyWeightRes.ok) setLatestBodyWeight(await bodyWeightRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatBodyWeightDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in" data-testid="dashboard-page">
      <div className="header">
        <div>
          <p className="text-muted" style={{ fontSize: '14px', marginBottom: '4px' }}>
            {t('dashboard.greeting', { name: user?.name?.split(' ')[0] || t('dashboard.default_athlete') })}
          </p>
          <h1 className="header-title">{t('dashboard.title')}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="notif-btn"
            onClick={() => navigate('/diets')}
            title={t('diets.page_title')}
          >
            <Scale size={20} />
            {dietCount > 0 && <span className="notif-badge">{dietCount > 9 ? '9+' : dietCount}</span>}
          </button>
          <button
            className="notif-btn"
            onClick={() => navigate('/reports')}
            title={t('reports.inbox_title')}
          >
            <Dumbbell size={20} />
            {notifCount > 0 && <span className="notif-badge">{notifCount > 9 ? '9+' : notifCount}</span>}
          </button>
          <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
        </div>
      </div>

      {/* ── Hero: Rutinas disponibles (protagonismo) ───────────── */}
      <div className="flex items-center justify-between mb-4">
        <h2 style={{ fontSize: '22px', color: 'var(--foreground)' }}>{t('dashboard.my_routines')}</h2>
      </div>

      {workouts.length === 0 ? (
        <div className="empty-state">
          <Dumbbell className="empty-icon" />
          <h3 className="empty-title">{t('dashboard.no_routines_title')}</h3>
          <p className="empty-desc">{t('dashboard.no_routines_desc')}</p>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateOptions(true)}
            data-testid="create-first-workout-btn"
          >
            <Plus size={20} />
            {t('dashboard.create_btn')}
          </button>
        </div>
      ) : (
        <div className="dashboard-routines-list">
          {workouts.map((workout) => (
            <div
              key={workout.workout_id}
              className="workout-card workout-card-hero"
              onClick={() => navigate(`/workout/${workout.workout_id}`)}
              data-testid={`workout-card-${workout.workout_id}`}
            >
              <div className="workout-card-header">
                <h3 className="workout-card-title">{workout.name}</h3>
                <span className="workout-card-days">{t('dashboard.days_count', { count: workout.days?.length || 0 })}</span>
              </div>
              {workout.description && (
                <p className="workout-card-desc">{workout.description}</p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', color: 'var(--primary)' }}>
                <ChevronRight size={16} />
                <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>{t('dashboard.view_details')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Resumen breve de estadísticas + CTA a /progress ────── */}
      {progress && (
        <DashboardStatsSummary
          progress={progress}
          onOpenProgress={() => navigate('/progress')}
          t={t}
        />
      )}

      {/* ── Peso corporal (acceso rápido) ──────────────────────── */}
      <BodyWeightQuickCard
        latestBodyWeight={latestBodyWeight}
        onClick={() => navigate('/body-weight')}
        formatBodyWeightDate={formatBodyWeightDate}
        t={t}
      />

      {user?.coach_id && (
        <button
          className="fab fab-report"
          onClick={() => navigate('/reports/new')}
          title={t('reports.send_title')}
        >
          <Dumbbell size={20} />
        </button>
      )}

      <button
        className="fab fab-coach"
        onClick={() => navigate('/link-coach')}
        title={t('dashboard.link_coach')}
      >
        <UserPlus size={22} />
      </button>

      <button className="fab" onClick={() => setShowCreateOptions(true)}>
        <Plus size={24} />
      </button>

      {showCreateOptions && createPortal(
        <CreateOptionsModal
          onClose={() => setShowCreateOptions(false)}
          navigate={navigate}
          t={t}
        />,
        document.body
      )}
    </div>
  );
};

// Compact 3-metric snapshot with CTA to the full ProgressPage. Keeps the
// dashboard scannable — full rankings + charts live in /progress now.
const DashboardStatsSummary = ({ progress, onOpenProgress, t }) => {
  const {
    weeklyProgressPercent = 0,
    stagnatedTotal = 0,
    declinedExercises = 0,
    totalComparedExercises = 0,
  } = progress || {};

  const hasData = totalComparedExercises > 0 || stagnatedTotal > 0;

  return (
    <div className="dashboard-stats-summary">
      <div className="dashboard-stats-summary-header">
        <h2 style={{ fontSize: '18px', color: 'var(--foreground)', margin: 0 }}>
          {t('dashboard.stats_summary_title')}
        </h2>
        <button
          className="dashboard-stats-cta"
          onClick={onOpenProgress}
          data-testid="dashboard-see-progress"
        >
          {t('dashboard.see_full_progress')}
          <ChevronRight size={14} />
        </button>
      </div>

      {hasData ? (
        <div className="dashboard-stats-row">
          <div className="dashboard-stat-mini">
            <TrendingUp size={14} className="dashboard-stat-icon" style={{ color: 'var(--primary)' }} />
            <span className="dashboard-stat-value">{Math.round(weeklyProgressPercent)}%</span>
            <span className="dashboard-stat-label">{t('dashboard.progress_card_weekly')}</span>
          </div>
          <div className="dashboard-stat-mini">
            <AlertTriangle size={14} className="dashboard-stat-icon" />
            <span className="dashboard-stat-value">{stagnatedTotal}</span>
            <span className="dashboard-stat-label">{t('dashboard.progress_card_stagnated')}</span>
          </div>
          <div className="dashboard-stat-mini">
            <TrendingDown size={14} className="dashboard-stat-icon" />
            <span className="dashboard-stat-value">{declinedExercises}</span>
            <span className="dashboard-stat-label">{t('dashboard.progress_card_decline')}</span>
          </div>
        </div>
      ) : (
        <p className="dashboard-stats-empty">{t('dashboard.progress_no_comparison')}</p>
      )}
    </div>
  );
};

// Quick access card to log / see body weight. Kept on the dashboard because
// the athlete uses it as a fast daily action.
const BodyWeightQuickCard = ({ latestBodyWeight, onClick, formatBodyWeightDate, t }) => (
  <div
    className="dashboard-body-weight-card"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    data-testid="body-weight-card"
  >
    <div className="dashboard-body-weight-icon">
      <Scale size={22} />
    </div>
    <div className="dashboard-body-weight-info">
      <span className="dashboard-body-weight-label">{t('dashboard.body_weight_title')}</span>
      {latestBodyWeight ? (
        <>
          <span className="dashboard-body-weight-value">
            {latestBodyWeight.weight.toFixed(1)}
            <span style={{ fontSize: 14, marginLeft: 4, color: 'var(--muted-foreground)' }}>kg</span>
          </span>
          <span className="dashboard-body-weight-sub">{formatBodyWeightDate(latestBodyWeight.date)}</span>
        </>
      ) : (
        <span className="dashboard-body-weight-sub">{t('dashboard.body_weight_empty')}</span>
      )}
    </div>
    <ChevronRight size={20} style={{ color: 'var(--muted-foreground)' }} />
  </div>
);

// Create-options modal with a "More" button that expands secondary
// actions (AI, water) with a genie-lamp scale+fade animation whose
// origin is the position of the "More" button that triggered it.
const CreateOptionsModal = ({ onClose, navigate, t }) => {
  const [expanded, setExpanded] = useState(false);
  const moreBtnRef = useRef(null);
  const [origin, setOrigin] = useState({ x: 50, y: 100 });

  const openExpanded = () => {
    const btn = moreBtnRef.current;
    if (btn) {
      const modal = btn.closest('.modal');
      if (modal) {
        const btnRect = btn.getBoundingClientRect();
        const modalRect = modal.getBoundingClientRect();
        setOrigin({
          x: ((btnRect.left + btnRect.width / 2 - modalRect.left) / modalRect.width) * 100,
          y: ((btnRect.top + btnRect.height / 2 - modalRect.top) / modalRect.height) * 100,
        });
      }
    }
    setExpanded(true);
  };

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ textAlign: 'center', marginBottom: '12px' }}>{t('dashboard.create_title')}</h2>
        <p style={{ textAlign: 'center', marginBottom: '24px', color: 'var(--muted-foreground)', fontSize: '14px' }}>
          {t('dashboard.create_question')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => go('/workout/create')}
            data-testid="create-manual-btn"
          >
            {t('dashboard.create_manual')}
          </button>

          {!expanded ? (
            <button
              ref={moreBtnRef}
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={openExpanded}
              data-testid="create-more-btn"
            >
              <MoreHorizontal size={18} />
              {t('dashboard.create_more')}
            </button>
          ) : (
            <div
              className="dashboard-more-options"
              style={{ transformOrigin: `${origin.x}% ${origin.y}%` }}
            >
              <button
                className="btn btn-secondary dashboard-more-option"
                style={{ width: '100%', animationDelay: '0ms' }}
                onClick={() => go('/create-with-ai')}
                data-testid="create-ai-btn"
              >
                {t('dashboard.create_ai')}
              </button>
              <button
                className="btn btn-secondary dashboard-more-option"
                style={{ width: '100%', animationDelay: '60ms' }}
                onClick={() => go('/water')}
                data-testid="open-water-btn"
              >
                <Droplet size={16} />
                {t('dashboard.open_water')}
              </button>
            </div>
          )}

          <button
            className="btn"
            style={{ width: '100%', marginTop: '8px' }}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
